import {
  IDestinationEntityJobRunOptions,
  ITranslationMap,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ClinicalChart,
  MockAllTeeth,
  TreatmentConfiguration,
} from '@principle-theorem/principle-core';
import {
  TreatmentConfigurationName,
  type IClinicalChart,
  type IPatient,
  type IStaffer,
  type ITreatmentConfiguration,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  Timezone,
  WithPrimaryKey,
  getError,
  snapshotCombineLatest,
  toMomentTz,
  toTimestamp,
  where,
  type IIdentifiable,
  type WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { combineLatest, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientClinicalChartDestinationEntity,
  IBaseClinicalChartJobData,
  IClinicalChartMigrationData,
  IGeneratedCharts,
  PATIENT_CLINICAL_CHART_RESOURCE_TYPE,
} from '../../../destination/entities/patient-clinical-charts';
import { PracticeMigration } from '../../../practice-migrations';
import { type TranslationMapHandler } from '../../../translation-map';
import { PatientAppointmentSourceEntity } from '../../source/entities/patient-appointments';
import {
  OasisPatientPeriodontalChart,
  PATIENT_PERIODONTAL_CHART_RESOURCE_TYPE,
  PatientPeriodontalChartSourceEntity,
} from '../../source/entities/patient-periodontal-charts';
import {
  OasisPatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { PROVIDER_RESOURCE_TYPE } from '../../source/entities/providers';
import { OasisItemCodeToTreatmentMappingHandler } from '../mappings/item-code-to-treatment';
import { OasisItemCodeMappingHandler } from '../mappings/item-codes';
import { OasisStafferMappingHandler } from '../mappings/staff';
import { PatientDestinationEntity } from './patients';
import { StafferDestinationEntity } from './staff';

export const PATIENT_CLINICAL_CHART_DESTINATION_ENTITY = DestinationEntity.init(
  {
    metadata: {
      key: PATIENT_CLINICAL_CHART_RESOURCE_TYPE,
      label: 'Patient Clinical Charts',
      description: `The conversion will be done as follows:
        Each Periodontal Chart will be added as a separate clinical chart.
      `,
    },
  }
);

export interface IClinicalChartJobData
  extends IBaseClinicalChartJobData<OasisPatient> {
  defaultTreatmentConfiguration: WithRef<ITreatmentConfiguration>;
  treatmentConfigurations: WithRef<ITreatmentConfiguration>[];
  treatmentConfigurationMappings: WithPrimaryKey<
    ITranslationMap<ITreatmentConfiguration>
  >[];
}

export class PatientClinicalChartDestinationEntity extends BasePatientClinicalChartDestinationEntity<
  OasisPatient,
  IClinicalChartJobData
> {
  destinationEntity = PATIENT_CLINICAL_CHART_DESTINATION_ENTITY;
  periodontalResourceType = PATIENT_PERIODONTAL_CHART_RESOURCE_TYPE;
  sourceCountComparison = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    appointments: new PatientAppointmentSourceEntity(),
    periodontalChart: new PatientPeriodontalChartSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
    staff: new StafferDestinationEntity(),
  };

  customMappings = {
    staff: new OasisStafferMappingHandler(),
    itemCodes: new OasisItemCodeMappingHandler(),
    itemCodeToTreatment: new OasisItemCodeToTreatmentMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IClinicalChartJobData[]> {
    const brand$ = PracticeMigration.brand$(migration);
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(PROVIDER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const sourceItemCodes$ =
      this.customMappings.itemCodes.getRecords(translationMap);
    const treatmentConfigurationMappings$ =
      this.customMappings.itemCodeToTreatment.getRecords(translationMap);
    const defaultTreatmentConfiguration$ = brand$.pipe(
      switchMap((brand) =>
        Firestore.firstResult(
          TreatmentConfiguration.col(brand),
          where('name', '==', TreatmentConfigurationName.GeneralADACode)
        )
      ),
      map((config) => {
        if (!config) {
          throw new Error(
            `Couldn't find default treatment configuration ${TreatmentConfigurationName.GeneralADACode}`
          );
        }
        return config;
      })
    );
    const treatmentConfigurations$ = brand$.pipe(
      switchMap((brand) => TreatmentConfiguration.all$(brand))
    );

    return snapshotCombineLatest([
      staff$,
      defaultTreatmentConfiguration$,
      treatmentConfigurations$,
      sourceItemCodes$,
      treatmentConfigurationMappings$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          sourcePatients,
          [
            staff,
            defaultTreatmentConfiguration,
            treatmentConfigurations,
            sourceItemCodes,
            treatmentConfigurationMappings,
          ],
        ]) =>
          sourcePatients.map((sourcePatient) => ({
            staff,
            sourcePatient,
            defaultTreatmentConfiguration,
            treatmentConfigurations,
            sourceItemCodes,
            treatmentConfigurationMappings,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IClinicalChartJobData
  ): Promise<
    | IClinicalChartMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = data.sourcePatient.data.id;
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId.toString(),
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient.record,
          `Couldn't resolve patient`
        );
      }

      const charts = await this._buildClinicalChartData(
        migration,
        sourcePatientId
      );

      return {
        sourcePatientId: sourcePatientId.toString(),
        patientRef,
        charts,
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  private async _buildClinicalChartData(
    migration: WithRef<IPracticeMigration>,
    patientUid: number
  ): Promise<IGeneratedCharts> {
    const sourcePerioCharts =
      await this.sourceEntities.periodontalChart.filterRecords(
        migration,
        'patientId',
        patientUid
      );

    const perioClinicalCharts = buildPerioCharts(
      sourcePerioCharts,
      migration.configuration.timezone
    );

    return {
      clinicalCharts: [],
      perioCharts: compact(perioClinicalCharts),
    };
  }
}

function buildPerioCharts(
  sourcePerioCharts: IGetRecordResponse<OasisPatientPeriodontalChart>[],
  timezone: Timezone
): (IClinicalChart & IIdentifiable)[] {
  return sourcePerioCharts.map((sourcePerioChart) => {
    const uid = new PatientPeriodontalChartSourceEntity().getSourceRecordId(
      sourcePerioChart.data
    );
    const createdAt = toTimestamp(
      toMomentTz(sourcePerioChart.data.date, timezone)
    );
    const perioRecords = sourcePerioChart.data.teeth;

    return {
      ...ClinicalChart.init({
        perioRecords,
        teeth: MockAllTeeth(),
        createdAt,
        createdBy: undefined,
        immutable: true,
      }),
      uid,
    };
  });
}
